<template>
  <v-col>
    <v-progress-circular v-if="isLoading" indeterminate color="loader" size="25" />
    <icon-tip
        v-if="noActions"
        icon="lock"
        :tip="$t('Нет доступных действий')" />
    <btn-icon-tip
        v-if="!loaded && !isLoading"
        :id="`showBtn-${lotId}`"
        icon="more_horiz"
        :tip="$t('Показать все доступные действия')"
        @click="showActions" />
    <route-btn-tip
        v-if="canRegistrate"
        :id="`registrateBtn-${lotId}`"
        :to="{ path: `${urlRootPath}/lotWizard/${lotType}/${lotId}` }"
        icon="edit"
        :tip="$t('Завершить регистрацию процесса торгов')" />
    <route-btn-tip
        v-if="(isAdmin || isOrganizer) && canRecall"
        :id="`recallBtn-${lotId}`"
        :to="{ path: `${urlRootPath}/lot/${lotType}/${lotId}/request/${LOT_REQUEST_TYPES.RECALL}` }"
        icon="cancel_presentation"
        :tip="$t('Отозвать торговую процедуру')" />
    <route-btn-tip
        v-if="canChangeDocuments"
        :id="`lotDocumentationBtn-${lotId}`"
        :to="{ path: `${urlRootPath}/lot/${lotType}/${lotId}/request/${LOT_REQUEST_TYPES.CHANGE_DOCUMENTS}` }"
        icon="insert_drive_file"
        :tip="$t('Изменение документации по торговой процедуре')" />
    <route-btn-tip
        v-if="canAddOrders"
        :id="`addOrdersBtn-${lotId}`"
        :to="{ path: `${urlRootPath}/lot/${lotType}/${lotId}/bidding` }"
        icon="gavel"
        :tip="$t('Подать ценовую заявку')" />
    <btn-icon-tip
        v-if="canDelete"
        :id="`deleteBtn-${lotId}`"
        icon="delete"
        :tip="$t('Удалить торговую процедуру')"
        @click="showConfirmationBeforDelete" />
    <btn-icon-tip
        v-if="canCreateTemplate"
        :id="`canCreateTemplateBtn-${lotId}`"
        icon="file_copy"
        :tip="$t('Сохранить как шаблон')"
        @click="saveAsTemplate" />
    <route-btn-tip
        v-if="canLoadTradeProcessDocumentation"
        :id="`tradeDocumentationBtn-${lotId}`"
        :to="{ path: `${urlRootPath}/lot/${lotType}/${lotId}/${LOT_TABS.TRADE_PROCESS_DOCUMENTATION}` }"
        icon="note_add"
        :tip="$t('Добавление документов о ходе торгов')" />
    <route-btn-tip
        v-if="canWinnerSelect"
        :id="`goToTradeResultsBtn-${lotId}`"
        :to="{ path: `${urlRootPath}/lot/${lotType}/${lotId}/${LOT_TABS.TRADE_RESULTS}` }"
        icon="assignment_turned_in"
        :tip="$t(lotStatus === LOT_STATUSES.REGISTERED
          ? $t('Подвести итоги')
          : $t('Изменить итоги'))" />
    <confirm-dialog
        v-if="confirmDialog.show"
        :dialog-prop="confirmDialog"
        warning
        @close="closeDialog('confirmDialog')" />
    <lot-recall-dialog
        v-if="recallDialog.show && (isAdmin || isOrganizer)"
        :dialog-prop="recallDialog"
        @close="closeDialog('recallDialog')" />

  </v-col>
</template>

<script>
  import IconTip from '@/components/reused/ui/IconTip.vue'
  import RouteBtnTip from '@/components/reused/ui/RouteBtnTip.vue'
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import ConfirmDialog from '@/components/reused/common/ConfirmDialog.vue'
  import LotRecallDialog from './LotRecallDialog.vue'
  import { LOT_STATUSES, LOT_TABS, LOT_REQUEST_TYPES, LOT_ACTIONS } from '@/constants/business'
  import { mapActions, mapGetters } from 'vuex'
  import { LOTS, LOT_MANAGEMENT, LOT_TEMPLATE_MANAGEMENT } from '@/constants/store.js'

  export default {
    components: {
      IconTip,
      RouteBtnTip,
      BtnIconTip,
      ConfirmDialog,
      LotRecallDialog
    },
    props: {
      lotId: {
        type: String,
        required: true
      },
      lotNumber: {
        type: [String, Number],
        required: true
      },
      privacyType: {
        type: String,
        required: true
      },
      lotType: {
        type: String,
        required: true
      },
      edited: {
        type: [Function, Promise]
      },
      lotStatus: {
        type: String,
        required: true
      },
      summarizing: {
        Type: Boolean,
        default: false
      }
    },
    data () {
      return {
        LOT_STATUSES,
        LOT_TABS,
        LOT_REQUEST_TYPES,
        loaded: false,
        isLoading: false,
        recallDialog: {
          lotType: null,
          lotId: null,
          show: false,
          callback: null
        },
        confirmDialog: {
          show: false,
          text: null,
          additional: null,
          callback: null,
          warning: true
        }
      }
    },
    computed: {
      ...mapGetters(LOTS.NAMESPACE, {
        actionsByLotId: LOTS.GETTERS.ACTIONS_BY_LOT_ID
      }),
      actions () {
        const lotActions = this.actionsByLotId(this.lotId) || {}
        return lotActions.actions || []
      },
      // для списка подведения итогов кнопку не отображаем
      canRegistrate () {
        return this.actions.includes(LOT_ACTIONS.REGISTRATION) && !this.summarizing
      },
      canCreateTemplate () {
        return this.actions.includes(LOT_ACTIONS.CREATE_TEMPLATE) && !this.summarizing
      },
      canRecall () {
        return this.actions.includes(LOT_ACTIONS.RECALL) && !this.summarizing
      },
      canChangeDocuments () {
        return this.actions.includes(LOT_ACTIONS.CHANGE_DOCUMENTS) && !this.summarizing
      },
      canAddOrders () {
        return this.actions.includes(LOT_ACTIONS.ADD_ORDERS) && !this.summarizing
      },
      canDelete () {
        return this.actions.includes(LOT_ACTIONS.DELETE) && !this.summarizing
      },
      canLoadTradeProcessDocumentation () {
        return this.actions.includes(LOT_ACTIONS.TRADE_PROCESS_DOCUMENTATION) && !this.summarizing
      },
      canWinnerSelect () {
        return this.actions.includes(LOT_ACTIONS.WINNER_SELECT)
      },
      noActions () {
        return this.loaded && !this.canRegistrate &&
          !this.canRecall && !this.canChangeDocuments &&
          !this.canAddOrders && !this.canDelete &&
          !this.canLoadTradeProcessDocumentation &&
          !this.canWinnerSelect && !this.canCreateTemplate
      }
    },
    created () {
      this.showActions()
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(LOTS.NAMESPACE, {
        getLotActionsAction: LOTS.ACTIONS.GET_LOT_ACTIONS,
        clearLotActionsAction: LOTS.ACTIONS.CLEAR_LOT_ACTIONS
      }),
      ...mapActions(LOT_MANAGEMENT.NAMESPACE, {
        deleteLotAction: LOT_MANAGEMENT.ACTIONS.DELETE_LOT
      }),
      ...mapActions(LOT_TEMPLATE_MANAGEMENT.NAMESPACE, {
        saveAsTemplateAction: LOT_TEMPLATE_MANAGEMENT.ACTIONS.SAVE_AS_TEMPLATE
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearLotActionsAction()
          ])
      },
      showActions: async function () {
        await this.clearStore()
        if (!this.privacyType || !this.lotId) {
          return
        }
        await this.getLotActions()
      },
      getLotActions: async function () {
        this.isLoading = true
        try {
          await this.getLotActionsAction({
            privacyType: this.privacyType,
            lotId: this.lotId,
            lotType: this.lotType
          })
          this.isLoading = false

          this.loaded = true
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      saveAsTemplate: async function () {
        try {
          await this.saveAsTemplateAction({
            lotId: this.lotId,
            lotType: this.lotType
          })
          this.goToRoute(`${this.urlRootPath}/templates`)
        } catch (error) {
          this.throwIfNotServerError(error)
        }
      },
      openRecallDialog: function () {
        this.recallDialog.lotType = this.lotType
        this.recallDialog.lotId = this.lotId
        this.recallDialog.callback = this.edited
        this.recallDialog.show = true
      },
      // открыть окно подтверждения перед удалением торговой процедуры
      showConfirmationBeforDelete: function () {
        this.confirmDialog.additional = {
          lotId: this.lotId,
          lotType: this.lotType
        }
        this.confirmDialog.text = this.$t('Торговая процедура будет удалена. Продолжить?')
        this.confirmDialog.callback = this.deleteLot
        this.confirmDialog.warning = true
        this.confirmDialog.show = true
      },
      deleteLot: async function (lot) {
        try {
          this.isLoading = true
          await this.deleteLotAction(lot)
          this.isLoading = false
          if (this.edited) {
            this.edited()
          }
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      }
    }
  }
</script>
